import { useSyncExternalStore } from "react";

/**
 * Theme state, held outside React.
 *
 * It lives in a module store rather than in context because two very
 * different consumers need it: the toggle button, which re-renders, and the
 * WebGL scene, which must *not* — pushing the theme through context would
 * remount the canvas on every switch and rebuild 320 nodes, 480 edges and
 * three shader programs to change two colours.
 *
 * `useSyncExternalStore` is the React 19 primitive for exactly this: an
 * external mutable source that components subscribe to.
 *
 * The initial value is read off `<html data-theme>`, which the bootstrap
 * script in index.html has already set from localStorage or the OS
 * preference. Resolving it here instead would mean the first paint is the
 * default theme and the correct one arrives a frame later — a white flash
 * for every dark-theme visitor, on every page load.
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

/** Kept in sync with the bootstrap script in index.html and --color-canvas. */
const PAGE_BACKGROUND: Record<Theme, string> = {
  light: "#fafafa",
  dark: "#0a0a0b",
};

function readAttribute(): Theme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

let current: Theme = readAttribute();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): Theme {
  return current;
}

export function setTheme(next: Theme): void {
  if (next === current) return;
  current = next;
  document.documentElement.dataset.theme = next;
  // Also set on the element itself, so the area outside <body> (overscroll
  // rubber-banding, the space beside a short page) matches rather than
  // staying whatever the bootstrap script painted.
  document.documentElement.style.backgroundColor = PAGE_BACKGROUND[next];

  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Private browsing, or storage disabled. The theme still applies for this
    // session; it just will not be remembered. Not worth telling anyone.
  }

  for (const listener of listeners) listener();
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, snapshot);
}

export function toggleTheme(): void {
  setTheme(current === "dark" ? "light" : "dark");
}
